import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2 } from "lucide-react";
import PondokIcon from "../components/PondokIcon";
import { toast } from "sonner";

export default function Login() {
  const { login, status } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (status === "authed") return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Selamat datang kembali!");
      nav("/dashboard");
    } catch (err) {
      const msg = err.response?.data?.detail || "Login gagal";
      toast.error(typeof msg === "string" ? msg : "Login gagal");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      {/* Left: Hero image */}
      <div className="hidden lg:block relative">
        <img
          src="https://images.unsplash.com/photo-1703769605297-cc74106244d9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwyfHxyYWphJTIwYW1wYXQlMjBpc2xhbmR8ZW58MHx8fHwxNzc3Nzk4NTgxfDA&ixlib=rb-4.1.0&q=85"
          alt="Raja Ampat"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#082618] via-[#082618]/60 to-transparent" />
        <div className="relative h-full flex flex-col justify-end p-12 text-[#FDFBF7]">
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#D16B4D]">PondokRaja · Booking Manager</div>
          <h2 className="font-heading text-4xl lg:text-5xl font-medium mt-3 leading-tight">
            Kelola tamu, kamar, dan laporan — semudah menyapa.
          </h2>
          <p className="text-sm text-[#FDFBF7]/80 mt-3 max-w-md">
            Booking, pembayaran, calendar, dan laporan keuangan — semua terpusat di satu tempat untuk homestay Anda.
          </p>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-8 bg-[#FDFBF7]">
        <form onSubmit={submit} className="w-full max-w-sm flex flex-col gap-6">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-[#D16B4D] flex items-center justify-center">
              <PondokIcon className="h-6 w-6 text-white" strokeWidth={1.8} />
            </div>
            <div>
              <div className="font-heading text-lg font-semibold text-[#172E21]">PondokRaja</div>
              <div className="text-xs text-[#586D61]">Booking Manager</div>
            </div>
          </div>

          <div>
            <h1 className="font-heading text-3xl font-medium text-[#172E21]">Masuk</h1>
            <p className="text-sm text-[#586D61] mt-1">Akses panel admin homestay Anda.</p>
          </div>

          <div>
            <Label className="text-sm font-medium">Email</Label>
            <Input
              data-testid="input-login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@homestay.com"
              className="mt-1.5 border-[#EBE6DE]"
              required
            />
          </div>
          <div>
            <Label className="text-sm font-medium">Password</Label>
            <Input
              data-testid="input-login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="mt-1.5 border-[#EBE6DE] font-mono"
              required
            />
          </div>

          <Button
            data-testid="btn-login-submit"
            type="submit"
            disabled={loading}
            className="bg-[#D16B4D] hover:bg-[#B8553B] text-white rounded-xl py-6"
          >
            {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Memproses…</> : "Masuk"}
          </Button>

          <div className="text-xs text-center -mt-2">
            <Link
              to="/forgot-password"
              data-testid="link-forgot-password"
              className="text-[#586D61] hover:text-[#D16B4D] hover:underline"
            >
              Lupa password?
            </Link>
          </div>

          <div className="text-xs text-center text-[#586D61] pt-2">
            Belum punya akun?{" "}
            <Link to="/" className="text-[#D16B4D] hover:underline font-medium" data-testid="link-back-landing">
              Coba Demo Gratis
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
